/**
 * Client-side gates for Placement Intelligence UI (Phase 5).
 * Mirrors backend COMPANY_INTEL_ENABLED + PLACEMENT_AI_INSIGHTS_ENABLED.
 */

function parseEnvFlag(value) {
  return String(value || 'false').trim().toLowerCase() === 'true';
}

export function isCompanyIntelEnabled() {
  return parseEnvFlag(process.env.REACT_APP_COMPANY_INTEL_ENABLED);
}

export function isPlacementAiInsightsEnabled() {
  return parseEnvFlag(process.env.REACT_APP_PLACEMENT_AI_INSIGHTS_ENABLED);
}

export function isPlacementInsightsUiEnabled() {
  return isCompanyIntelEnabled() && isPlacementAiInsightsEnabled();
}

export function isPlacementBatchAnalyticsEnabled() {
  return parseEnvFlag(process.env.REACT_APP_PLACEMENT_BATCH_ANALYTICS_ENABLED);
}

export function isPlacementBatchAnalyticsUiEnabled() {
  return isPlacementInsightsUiEnabled() && isPlacementBatchAnalyticsEnabled();
}

export function isPlacementAiCoachEnabled() {
  return parseEnvFlag(process.env.REACT_APP_PLACEMENT_AI_COACH_ENABLED);
}

/** Phase 9: AI coach UI requires the insights UI plus its own flag. */
export function isPlacementCoachUiEnabled() {
  return isPlacementInsightsUiEnabled() && isPlacementAiCoachEnabled();
}

/**
 * Faculty Journey experience layer (Detect → Assign → Monitor). Requires the
 * batch-analytics UI plus its own flag. Mirrors backend FACULTY_JOURNEY_ENABLED.
 */
export function isFacultyJourneyEnabled() {
  return parseEnvFlag(process.env.REACT_APP_FACULTY_JOURNEY_ENABLED);
}

export function isFacultyJourneyUiEnabled() {
  return isPlacementBatchAnalyticsUiEnabled() && isFacultyJourneyEnabled();
}

/**
 * Mock Interviews UI (RC-006). Fail closed — must mirror backend MOCK_INTERVIEWS_ENABLED.
 * When OFF, student/faculty mock surfaces should not assume API availability.
 */
export function isMockInterviewsEnabled() {
  return parseEnvFlag(process.env.REACT_APP_MOCK_INTERVIEWS_ENABLED);
}

/** Sub-flags require master mock interviews ON. */
function isMockSubFlagEnabled(envName) {
  return isMockInterviewsEnabled() && parseEnvFlag(process.env[envName]);
}

export function isMockInterviewWorkspaceEnabled() {
  return isMockSubFlagEnabled('REACT_APP_MOCK_INTERVIEW_WORKSPACE_ENABLED');
}

export function isMockInterviewSchedulingUxEnabled() {
  return isMockSubFlagEnabled('REACT_APP_MOCK_INTERVIEW_SCHEDULING_UX_ENABLED');
}

export function isMockInterviewCanonicalMetricsEnabled() {
  if (!isMockInterviewsEnabled()) return false;
  const raw = process.env.REACT_APP_MOCK_INTERVIEW_CANONICAL_METRICS_ENABLED;
  if (raw == null || raw === '') return true;
  return parseEnvFlag(raw);
}

export function isMockInterviewExportEnabled() {
  return isMockSubFlagEnabled('REACT_APP_MOCK_INTERVIEW_EXPORT_ENABLED');
}

export function isMockInterviewDisplayScoreEnabled() {
  return isMockSubFlagEnabled('REACT_APP_MOCK_INTERVIEW_DISPLAY_SCORE_ENABLED');
}

export function isMockInterviewAiNotesEnabled() {
  return isMockSubFlagEnabled('REACT_APP_MOCK_INTERVIEW_AI_NOTES_ENABLED');
}

export function isMockInterviewSessionRubricEditEnabled() {
  return isMockSubFlagEnabled('REACT_APP_MOCK_INTERVIEW_SESSION_RUBRIC_EDIT_ENABLED');
}

/** Company Readiness Action Center is part of placement insights (no separate flag). */
export function isCompanyActionCenterUiEnabled() {
  return isPlacementInsightsUiEnabled();
}

export const MOCK_INTERVIEWS_DISABLED_MESSAGE =
  'Mock interviews are not enabled yet.';

export const FACULTY_JOURNEY_DISABLED_MESSAGE =
  'Company preparation is not turned on yet.';

export const PLACEMENT_COACH_DISABLED_MESSAGE =
  'The AI placement coach is not enabled yet.';

export const PLACEMENT_INSIGHTS_DISABLED_MESSAGE =
  'Placement Intelligence is not enabled yet.';

export const PLACEMENT_BATCH_ANALYTICS_DISABLED_MESSAGE =
  'Placement batch analytics are not enabled yet.';
